import Enterable from '@/components/Enterable';
import PosButton from '@/components/PosButton';
import { emitGlobalEvent } from '@/components/QRScanner/GlobalScanner';
import RechargeOptionalWrapper from '@/components/RechargeOptionalWrapper';
import { useFetchMoremeClientName } from '@/models/moreme';
import { UserFullInfo } from '@/models/user';
import { playSound } from '@/utils/sound';
import {
  DisconnectOutlined,
  LinkOutlined,
  MoneyCollectOutlined,
  PoweroffOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { Button, Space, Spin } from 'antd';
import { createStyles } from 'antd-style';
import React, { useCallback, useEffect, useState } from 'react';
import { ImmerProducer } from '../../global';
import { UserBasicInfo } from './UserBasicInfo';
import { TabTypes, UserExtraInfo } from './UserExtraInfo';

const useStyles = createStyles(({ css, token }) => {
  return {
    container: css`
      user-select: none;
      z-index: 1;
      position: relative;
    `,
    operations: css`
      position: absolute;
      right: 2px;
      top: 20px;
      z-index: -1;
      transform: translate(100%, 0);
      .ant-btn {
        border: none;
        border-radius: 0 5px 5px 0;
        &:disabled {
          &.ant-btn-primary {
            background: ${token.primary5};
            color: #ddd;
          }
          &.ant-btn-default {
            background: white;
            color: #ddd;
          }
        }
      }
    `,
    border: css`
      width: 100%;
      height: 2px;
      border: 2px solid ${token.colorPrimary};
    `,
    footer: css`
      display: flex;
      flex-direction: column;
      .ant-btn {
        width: 100%;
        border-radius: 0;
      }
    `,
  };
});

export type Props = {
  onRequestBind?: (userFullInfo: UserFullInfo) => void;
  onRequestRecharge?: (userFullInfo: UserFullInfo) => void;
  onRequestUnbind?: (userFullInfo: UserFullInfo) => void;
  userInfo?: UserFullInfo;
  showCloseBtn?: boolean;
  showOperations?: boolean;
  onRequestClose?: () => void;
  onRequestReload?: () => void;
  loading?: boolean;
  updateUserFullInfo: ImmerProducer<UserFullInfo>;
};

const UserInfo: React.FC<Props> = ({
  userInfo: info,
  showOperations = false,
  showCloseBtn,
  onRequestBind,
  onRequestRecharge,
  onRequestUnbind,
  onRequestClose,
  onRequestReload,
  loading = false,
  updateUserFullInfo,
}: Props) => {
  const { styles } = useStyles();
  const [activeKey, setActiveKey] = useState<TabTypes>(null);
  const [version, setVersion] = useState(0);
  const { payload: moremeClientName, send: fetchMoremeClientName } = useFetchMoremeClientName();

  useEffect(() => {
    if (activeKey) playSound('click');
  }, [activeKey]);

  const switchToTab = (key: TabTypes) => {
    setActiveKey(key);
  };

  const reload = useCallback(() => {
    setVersion(v => v + 1);
    onRequestReload();
  }, [onRequestReload, info]);

  const user = info?.user;

  useEffect(() => {
    if (user && user.moremeCid && !moremeClientName) {
      fetchMoremeClientName({
        method: 'get',
        pathVars: {
          username: user.username,
        },
      });
    }
  }, [user, moremeClientName]);

  const bindUser = useCallback(() => {
    playSound('click');
    emitGlobalEvent({ type: 'actionUserBind', payload: info });
    onRequestBind?.(info);
  }, [info]);

  const canOperate = Boolean(!loading && info);

  return (
    <>
      {showOperations && (
        <div className={styles.operations}>
          <Space direction={'vertical'}>
            <PosButton
              disabled={!canOperate}
              icon={<DisconnectOutlined />}
              onClick={() => {
                emitGlobalEvent({ type: 'actionUserUnBind', payload: info });
                onRequestUnbind?.(info);
              }}>
              取消关联
            </PosButton>
            <PosButton disabled={!canOperate} type={'primary'} icon={<LinkOutlined />} onClick={bindUser}>
              <Enterable showIcon onEnter={bindUser}>
                关联用户
              </Enterable>
            </PosButton>
            <RechargeOptionalWrapper userInfo={user}>
              <PosButton
                disabled={!canOperate}
                type={'primary'}
                icon={<MoneyCollectOutlined />}
                onClick={() => {
                  emitGlobalEvent({ type: 'actionUserRecharge', payload: info });
                  onRequestRecharge?.(info);
                }}>
                账户充值
              </PosButton>
            </RechargeOptionalWrapper>
            {onRequestReload && (
              <PosButton loading={loading} type={'primary'} icon={<ReloadOutlined />} onClick={reload}>
                更新信息
              </PosButton>
            )}
            <PosButton
              danger
              icon={<PoweroffOutlined />}
              onClick={() => {
                onRequestClose();
              }}>
              关闭
            </PosButton>
          </Space>
        </div>
      )}

      <div className={styles.container}>
        <div className={styles.border} />
        <>
          <Spin spinning={loading}>
            <UserBasicInfo loading={loading} info={info} onReload={reload} />

            <UserExtraInfo
              loading={loading}
              info={info}
              onReload={reload}
              version={version}
              moremeClientName={moremeClientName}
              activeKey={activeKey}
              switchToTab={switchToTab}
            />
          </Spin>
        </>
        <div className={styles.footer}>
          {showCloseBtn && (
            <Button
              size={'large'}
              type={'primary'}
              onClick={() => {
                onRequestClose();
              }}>
              关闭
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default UserInfo;
